<template>
  <div class="preproduction-create">
    <div class="page-header">
      <h2>创建预生产订单</h2>
      <a-button @click="$router.back()">
        <template #icon>
          <ArrowLeftOutlined />
        </template>
        返回
      </a-button>
    </div>

    <a-form
      ref="formRef"
      :model="form"
      :rules="rules"
      layout="vertical"
      :scroll-to-first-error="true"
    >
      <a-row :gutter="24">
        <a-col :span="16">
          <a-card title="基本信息" class="form-card">
            <a-form-item label="工厂" name="factoryId">
              <a-select
                v-model:value="form.factoryId"
                placeholder="请选择工厂"
                show-search
                :filter-option="filterOption"
              >
                <a-select-option
                  v-for="factory in factories"
                  :key="factory.id"
                  :value="factory.id"
                  :label="factory.name"
                >
                  {{ factory.name }}
                </a-select-option>
              </a-select>
            </a-form-item>

            <a-form-item label="订单日期" name="preProductionDate">
              <a-date-picker
                v-model:value="form.preProductionDate"
                format="YYYY-MM-DD"
                style="width: 100%"
              />
            </a-form-item>

            <a-form-item label="备注" name="remarks">
              <a-textarea
                v-model:value="form.remarks"
                placeholder="请输入备注"
                :rows="3"
                maxlength="500"
                show-count
              />
            </a-form-item>
          </a-card>

          <a-card title="订单明细" class="form-card">
            <div class="items-section">
              <!-- SKU选择区域 -->
              <div class="sku-selection" style="margin-bottom: 16px;">
                <a-row :gutter="16">
                  <a-col :span="6">
                    <a-form-item label="选择分类">
                      <a-tree-select
                        v-model:value="selectedCategoryId"
                        :tree-data="categoryOptions"
                        :field-names="{ label: 'name', value: 'id', children: 'children' }"
                        placeholder="请选择商品分类"
                        @change="onCategoryChange"
                        style="width: 100%"
                        :loading="categoriesLoading"
                        :disabled="categoriesLoading"
                        allow-clear
                      />
                    </a-form-item>
                  </a-col>
                  <a-col :span="6">
                    <a-form-item label="选择商品">
                      <a-select
                        v-model:value="selectedProductId"
                        placeholder="请选择商品"
                        @change="onProductSelect"
                        show-search
                        :filter-option="filterOption"
                        :loading="productsLoading"
                        :disabled="productsLoading"
                        allow-clear
                      >
                        <a-select-option
                          v-for="product in products"
                          :key="product.id"
                          :value="product.id"
                          :label="product.name"
                        >
                          {{ product.name }}
                        </a-select-option>
                      </a-select>
                    </a-form-item>
                  </a-col>
                  <a-col :span="6">
                    <a-form-item label="选择仓库">
                      <a-select
                        v-model:value="selectedWarehouseId"
                        placeholder="请选择仓库"
                      >
                        <a-select-option
                          v-for="warehouse in warehouses"
                          :key="warehouse.id"
                          :value="warehouse.id"
                        >
                          {{ warehouse.name }}
                        </a-select-option>
                      </a-select>
                    </a-form-item>
                  </a-col>
                  <a-col :span="6">
                    <a-form-item label="&nbsp;">
                      <a-button
                        type="primary"
                        @click="addToList"
                        :disabled="!selectedProductId || !selectedWarehouseId || availableSkus.length === 0"
                        style="width: 100%"
                      >
                        添加到列表
                      </a-button>
                    </a-form-item>
                  </a-col>
                </a-row>
              </div>

              <!-- SKU网格显示 -->
              <div v-if="!selectedProductId || !selectedWarehouseId" class="empty-sku">
                <a-empty description="请先选择商品和仓库" :image="simpleImage" />
              </div>

              <div v-else-if="availableSkus.length === 0" class="empty-sku">
                <a-empty description="该商品暂无SKU信息" :image="simpleImage" />
              </div>

              <div v-else class="sku-grid">
                <div style="border: 1px solid #d9d9d9; border-radius: 6px; padding: 16px; background: #fafafa;">
                  <!-- 表头 -->
                  <div style="display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 12px; margin-bottom: 12px; font-weight: 500; color: #666;">
                    <div style="text-align: center;">颜色</div>
                    <div style="text-align: center;">尺码</div>
                    <div style="text-align: center;">计划数量</div>
                  </div>

                  <!-- SKU选项列表 -->
                  <div style="max-height: 250px; overflow-y: auto;">
                    <div
                      v-for="sku in availableSkus"
                      :key="sku.key"
                      class="sku-row"
                      style="display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 12px; padding: 8px; border-radius: 4px; margin-bottom: 4px; align-items: center; background: white;"
                    >
                      <div style="text-align: center; font-weight: 500;">{{ sku.color }}</div>
                      <div style="text-align: center; font-weight: 500;">{{ sku.size }}</div>
                      <div style="text-align: center;">
                        <a-input-number
                          v-model:value="sku.plannedQuantity"
                          :min="0"
                          placeholder="0"
                          size="small"
                          style="width: 100px;"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- 已添加的明细列表 -->
              <div v-if="form.items.length > 0" style="margin-top: 24px;">
                <div style="margin-bottom: 12px; font-weight: 500;">已添加明细</div>
                <div
                  v-for="(item, index) in form.items"
                  :key="index"
                  class="item-row"
                >
                  <a-row :gutter="16" align="middle">
                    <a-col :span="6">
                      <div style="padding: 4px 0;">
                        <div style="font-size: 12px; color: #999;">商品</div>
                        <div style="font-weight: 500;">{{ item.productName }}</div>
                      </div>
                    </a-col>

                    <a-col :span="5">
                      <div style="padding: 4px 0;">
                        <div style="font-size: 12px; color: #999;">仓库</div>
                        <div>{{ item.warehouseName }}</div>
                      </div>
                    </a-col>

                    <a-col :span="5">
                      <div style="padding: 4px 0;">
                        <div style="font-size: 12px; color: #999;">SKU</div>
                        <div>{{ item.color || '默认' }} / {{ item.size || '默认' }}</div>
                      </div>
                    </a-col>

                    <a-col :span="4">
                      <div style="padding: 4px 0;">
                        <div style="font-size: 12px; color: #999;">计划数量</div>
                        <div style="font-weight: 500; color: #1890ff;">{{ item.plannedQuantity }}</div>
                      </div>
                    </a-col>

                    <a-col :span="4">
                      <a-button
                        type="text"
                        danger
                        @click="removeItem(index)"
                      >
                        <template #icon>
                          <DeleteOutlined />
                        </template>
                        删除
                      </a-button>
                    </a-col>
                  </a-row>
                </div>
              </div>
            </div>
          </a-card>
        </a-col>

        <a-col :span="8">
          <a-card title="订单汇总" class="form-card summary-card">
            <a-descriptions :column="1" bordered>
              <a-descriptions-item label="工厂">
                {{ getFactoryName() || '-' }}
              </a-descriptions-item>
              <a-descriptions-item label="订单日期">
                {{ form.preProductionDate ? form.preProductionDate.format('YYYY-MM-DD') : '-' }}
              </a-descriptions-item>
              <a-descriptions-item label="商品种类">
                <a-tag color="blue">{{ uniqueProductCount }}</a-tag>
              </a-descriptions-item>
              <a-descriptions-item label="SKU数量">
                <a-tag color="blue">{{ form.items.length }}</a-tag>
              </a-descriptions-item>
              <a-descriptions-item label="总计划数量">
                <a-tag color="green" style="font-size: 16px; font-weight: bold;">{{ totalPlannedQuantity }}</a-tag>
              </a-descriptions-item>
            </a-descriptions>
          </a-card>
        </a-col>
      </a-row>

      <div class="form-actions">
        <a-button type="primary" size="large" :loading="loading" @click="handleSubmit">
          创建订单
        </a-button>
        <a-button size="large" @click="$router.back()" style="margin-left: 16px">
          取消
        </a-button>
      </div>
    </a-form>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { message, Empty } from 'ant-design-vue'
import {
  ArrowLeftOutlined,
  DeleteOutlined
} from '@ant-design/icons-vue'
import dayjs from 'dayjs'
import { preProductionApi, factoryApi, productApi, warehouseApi, categoryApi } from '@/api'

export default {
  name: 'PreProductionCreate',
  components: {
    ArrowLeftOutlined,
    DeleteOutlined
  },
  setup() {
    const router = useRouter()
    const formRef = ref(null)
    const loading = ref(false)
    const categoriesLoading = ref(false)
    const productsLoading = ref(false)
    const factories = ref([])
    const products = ref([])
    const warehouses = ref([])
    const categoryOptions = ref([])
    const selectedCategoryId = ref(null)
    const selectedProductId = ref(null)
    const selectedWarehouseId = ref(null)
    const availableSkus = ref([])

    const form = reactive({
      factoryId: undefined,
      preProductionDate: dayjs(),
      remarks: '',
      items: []
    })

    const rules = {
      factoryId: [
        { required: true, message: '请选择工厂', trigger: 'change' }
      ],
      preProductionDate: [
        { required: true, message: '请选择订单日期', trigger: 'change' }
      ]
    }

    const simpleImage = Empty.PRESENTED_IMAGE_SIMPLE

    const uniqueProductCount = computed(() => {
      const productIds = new Set(form.items.map(item => item.productId).filter(Boolean))
      return productIds.size
    })

    const totalPlannedQuantity = computed(() => {
      return form.items.reduce((sum, item) => sum + (item.plannedQuantity || 0), 0)
    })

    const getFactoryName = () => {
      const factory = factories.value.find(f => f.id === form.factoryId)
      return factory ? factory.name : ''
    }

    const filterOption = (input, option) => {
      return (option.label || '').toLowerCase().includes(input.toLowerCase())
    }

    const loadFactories = async () => {
      try {
        const result = await factoryApi.all()
        if (result.code === '0') {
          factories.value = result.data || []
          if (factories.value.length === 1) {
            form.factoryId = factories.value[0].id
          }
        }
      } catch (error) {
        console.error('加载工厂列表失败:', error)
      }
    }

    const loadCategories = async () => {
      categoriesLoading.value = true
      try {
        const result = await categoryApi.getTree()
        if (result.code === '0') {
          categoryOptions.value = result.data || []
        }
      } catch (error) {
        console.error('加载分类失败:', error)
        categoryOptions.value = []
      } finally {
        categoriesLoading.value = false
      }
    }

    const loadProducts = async (categoryId = null) => {
      productsLoading.value = true
      try {
        const queryParams = {
          status: 1
        }

        if (categoryId) {
          queryParams.categoryId = categoryId
        }

        const result = await productApi.querySimpleInfo(queryParams)
        if (result.code === '0') {
          products.value = result.data || []
        }
      } catch (error) {
        console.error('加载商品列表失败:', error)
        products.value = []
      } finally {
        productsLoading.value = false
      }
    }

    const loadWarehouses = async () => {
      try {
        const result = await warehouseApi.list()
        if (result.code === '0') {
          warehouses.value = result.data || []
          if (warehouses.value.length === 1) {
            selectedWarehouseId.value = warehouses.value[0].id
          }
        }
      } catch (error) {
        console.error('加载仓库列表失败:', error)
      }
    }

    const onCategoryChange = (categoryId) => {
      selectedCategoryId.value = categoryId
      selectedProductId.value = null
      availableSkus.value = []
      // 根据选择的分类加载商品
      loadProducts(categoryId)
    }

    const onProductSelect = async (productId) => {
      if (!productId) {
        availableSkus.value = []
        return
      }

      try {
        const result = await productApi.detail(productId)
        if (result.code === '0' && result.data) {
          const product = result.data

          // 提取所有唯一的颜色和尺码组合
          const skuMap = new Map()
          if (product.stockItems) {
            product.stockItems.forEach(item => {
              const key = `${item.color || ''}_${item.size || ''}`
              if (!skuMap.has(key)) {
                skuMap.set(key, {
                  key,
                  color: item.color || '默认',
                  size: item.size || '默认',
                  plannedQuantity: 0
                })
              }
            })
          }

          availableSkus.value = Array.from(skuMap.values())
        }
      } catch (error) {
        console.error('加载商品详情失败:', error)
        message.error('加载商品详情失败')
      }
    }

    const addToList = () => {
      if (!selectedProductId.value || !selectedWarehouseId.value) {
        message.warning('请先选择商品和仓库')
        return
      }

      const selectedSkus = availableSkus.value.filter(sku => sku.plannedQuantity > 0)
      if (selectedSkus.length === 0) {
        message.warning('请至少为一个SKU输入计划数量')
        return
      }

      const product = products.value.find(p => p.id === selectedProductId.value)
      const warehouse = warehouses.value.find(w => w.id === selectedWarehouseId.value)

      selectedSkus.forEach(sku => {
        const existingIndex = form.items.findIndex(item =>
          item.productId === selectedProductId.value &&
          item.warehouseId === selectedWarehouseId.value &&
          item.color === (sku.color === '默认' ? '' : sku.color) &&
          item.size === (sku.size === '默认' ? '' : sku.size)
        )

        const newItem = {
          productId: selectedProductId.value,
          productName: product.name,
          warehouseId: selectedWarehouseId.value,
          warehouseName: warehouse.name,
          color: sku.color === '默认' ? '' : sku.color,
          size: sku.size === '默认' ? '' : sku.size,
          plannedQuantity: sku.plannedQuantity
        }

        if (existingIndex >= 0) {
          form.items[existingIndex].plannedQuantity += sku.plannedQuantity
        } else {
          form.items.push(newItem)
        }
      })

      message.success(`已添加 ${selectedSkus.length} 个SKU`)

      // 清空选择
      selectedCategoryId.value = null
      selectedProductId.value = null
      selectedWarehouseId.value = warehouses.value.length === 1 ? warehouses.value[0].id : null
      availableSkus.value = []
      // 重新加载所有商品
      loadProducts()
    }

    const removeItem = (index) => {
      form.items.splice(index, 1)
    }

    const handleSubmit = async () => {
      try {
        await formRef.value.validateFields()

        if (form.items.length === 0) {
          message.error('请至少添加一个SKU')
          return
        }

        loading.value = true

        const result = await preProductionApi.create({
          factoryId: form.factoryId,
          preProductionDate: form.preProductionDate.format('YYYYMMDD'),
          remarks: form.remarks,
          items: form.items.map(item => ({
            productId: item.productId,
            warehouseId: item.warehouseId,
            color: item.color || undefined,
            size: item.size || undefined,
            plannedQuantity: item.plannedQuantity,
            remarks: ''
          }))
        })

        if (result.code === '0') {
          message.success('创建预生产订单成功')
          router.push('/production/preproduction')
        } else {
          message.error(result.msg || '创建预生产订单失败')
        }
      } catch (error) {
        if (error.errorFields) {
          return
        }
        console.error('创建预生产订单失败:', error)
        message.error('创建预生产订单失败')
      } finally {
        loading.value = false
      }
    }

    onMounted(() => {
      loadFactories()
      loadCategories()
      loadProducts()
      loadWarehouses()
    })

    return {
      formRef,
      loading,
      categoriesLoading,
      productsLoading,
      factories,
      products,
      warehouses,
      categoryOptions,
      selectedCategoryId,
      selectedProductId,
      selectedWarehouseId,
      availableSkus,
      form,
      rules,
      simpleImage,
      uniqueProductCount,
      totalPlannedQuantity,
      getFactoryName,
      filterOption,
      onCategoryChange,
      onProductSelect,
      addToList,
      removeItem,
      handleSubmit
    }
  }
}
</script>

<style scoped>
.preproduction-create {
  padding: 0;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.page-header h2 {
  margin: 0;
}

.form-card {
  margin-bottom: 24px;
}

.empty-sku {
  padding: 30px 0;
  text-align: center;
}

.sku-row:hover {
  background: #f0f0f0 !important;
}

.item-row {
  padding: 12px;
  margin-bottom: 12px;
  background: #f9f9f9;
  border-radius: 6px;
}

.form-actions {
  margin-top: 24px;
  text-align: center;
  padding: 24px 0;
  border-top: 1px solid #f0f0f0;
}

.summary-card {
  position: sticky;
  top: 24px;
}
</style>
